import { useNavigate } from "react-router-dom-v5-compat";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@apac/ui";
import { useModuleBEM } from "@b2bportal/core-themes";
import { ProductType } from "@b2bportal/core-types";
import { RestrictionCancelEnum } from "@b2bportal/ground-api";
import {
  type PackageItinerary,
  type Reservation,
  ReservationEnum,
} from "@b2bportal/rbc-bundled-packages-api";
import { useI18nContext } from "@hopper-b2b/i18n";
import { usePackageTripStatus } from "../../hooks/usePackageTripStatus";
import { PackageCheckoutBreakdown } from "../PackageCheckoutBreakdown";
import styles from "./PackageReservationDetails.module.scss";
import { ManageBooking } from "@/components";
import { TripHeader } from "@/components/shared/TripHeader/TripHeader";
import { TripsReservationStatusPill } from "@/components/shared/TripsReservationStatusPill/TripsReservationStatusPill";
import { MyTripPageHeader } from "@/overrides/apac/apac-ui/components/MyTripPageHeader/MyTripPageHeader";
import { CarReservationDetailSummary } from "@/overrides/cars/postbooking/components/CarReservationDetailSummary/CarReservationDetailSummary";
import { FlightSegmentCard } from "@/overrides/flights/components/FlightItineraryDetailPage/components/FlightDetailsPanel/FlightSegmentCard";
import { useFlightDetailsPanel } from "@/overrides/flights/components/FlightItineraryDetailPage/components/FlightDetailsPanel/useFlightDetailsPanel";
import { TripSummaryCard } from "@/overrides/hotels/components/HotelReservationPage/components/TripSummaryCard/TripSummaryCard";

interface PackageReservationDetailsProps {
  itinerary: PackageItinerary;
  tripsRoute: string;
}

function extractReservations(reservations: Reservation[]) {
  return {
    hotel: reservations.find((r) => r.Reservation === ReservationEnum.Hotel),
    flight: reservations.find((r) => r.Reservation === ReservationEnum.Flight),
    ground: reservations.find((r) => r.Reservation === ReservationEnum.Ground),
  };
}

export const PackageReservationDetails = ({
  itinerary,
  tripsRoute,
}: PackageReservationDetailsProps) => {
  const [, cn] = useModuleBEM(styles, "PackageReservationDetails");
  const { t } = useI18nContext();
  const navigate = useNavigate();

  const { hotel, flight, ground } = extractReservations(itinerary.reservations);

  // Opaque data access via optional chaining
  const hotelData = (hotel as any)?.hotel;
  const flightData = (flight as any)?.flight;
  const groundData = (ground as any)?.ground;

  const { state, isPast, isActive } = usePackageTripStatus(
    itinerary.hopperReservationId
  );

  const destinationName =
    ((itinerary as any).destinationName as string | undefined) ??
    hotelData?.reservation?.lodgingData?.address?.city ??
    hotelData?.reservation?.lodgingData?.city ??
    undefined;

  const { pricingBreakdown, paymentBreakdown } = itinerary;

  // Reuse the flight detail panel hook for slice summaries
  const { sliceSummaries } = useFlightDetailsPanel({
    itinerary: (flightData ?? {}) as any,
    airlineMap: itinerary.context?.airlines as any,
  });

  return (
    <>
      <MyTripPageHeader className={cn("page-header")} />

      <div className={cn("card-wrapper")}>
        <TripHeader
          statusPill={
            state != null ? (
              <TripsReservationStatusPill
                variant={ProductType.PACKAGE}
                status={state}
                isPastTrip={isPast}
              />
            ) : undefined
          }
          title={
            destinationName
              ? t("packageTrips.yourPackageFor", {
                  destination: destinationName,
                })
              : t("packageTrips.yourPackage")
          }
          confirmationLabel={t("hotelTrips.confirmationCode")}
          confirmationCode={itinerary.customerConfirmationId}
        />

        <div className={cn("two-column")}>
          {/* Hotel section — reuses TripSummaryCard from hotel detail page */}
          {hotelData?.reservation && (
            <TripSummaryCard
              reservation={hotelData.reservation}
              title={
                <h2 className={cn("section-heading")}>
                  {t("packageTrips.hotelSectionTitle")}
                </h2>
              }
              cta={
                hotelData.reservation.reservationBookingId && (
                  <button
                    className={cn("view-modify-btn")}
                    onClick={() =>
                      navigate(
                        `${tripsRoute}/hotels/${encodeURIComponent(hotelData.reservation.reservationBookingId)}?fromPackage=${encodeURIComponent(itinerary.hopperReservationId)}`
                      )
                    }
                  >
                    {t("packageTrips.viewOrModify")}
                  </button>
                )
              }
            />
          )}

          {/* Flight section — reuses FlightSegmentCard from flight detail page */}
          {sliceSummaries.length > 0 && (
            <div className={cn("section")}>
              <h2 className={cn("section-heading")}>
                {t("packageTrips.flightSectionTitle")}
              </h2>
              <div className={cn("flight-cards")}>
                {sliceSummaries.map(
                  (summary, index) =>
                    summary && (
                      <FlightSegmentCard
                        key={index}
                        segment={summary.firstSegment}
                        lastSegment={summary.lastSegment}
                        totalDurationMinutes={summary.totalDurationMinutes}
                        stopsCount={summary.stopsCount}
                        displayDestinationCode={summary.displayDestinationCode}
                        airportMap={itinerary.context?.airports as any}
                        airlineMap={itinerary.context?.airlines as any}
                      />
                    )
                )}
              </div>
              {flightData?.bookedItinerary?.id && (
                <button
                  className={cn("view-modify-btn")}
                  onClick={() =>
                    navigate(
                      `${tripsRoute}/flights/${encodeURIComponent(flightData.bookedItinerary.id)}?fromPackage=${encodeURIComponent(itinerary.hopperReservationId)}`
                    )
                  }
                >
                  {t("packageTrips.viewOrModify")}
                </button>
              )}
            </div>
          )}

          {/* Car section — reuses CarReservationDetailSummary from car detail page */}
          {groundData && (
            <CarReservationDetailSummary
              reservation={groundData}
              title={
                <h2 className={cn("section-heading")}>
                  {t("packageTrips.carSectionTitle")}
                </h2>
              }
              cta={
                groundData.internalGroundBookingId && (
                  <button
                    className={cn("view-modify-btn")}
                    onClick={() =>
                      navigate(
                        `${tripsRoute}/cars/${encodeURIComponent(groundData.internalGroundBookingId)}?fromPackage=${encodeURIComponent(itinerary.hopperReservationId)}`
                      )
                    }
                  >
                    {t("packageTrips.viewOrModify")}
                  </button>
                )
              }
            />
          )}

          {/* Checkout breakdown */}
          <PackageCheckoutBreakdown
            pricingBreakdown={pricingBreakdown}
            paymentBreakdown={paymentBreakdown}
            hotelData={hotelData}
            flightData={flightData}
            hasGround={!!groundData}
          />

          {/* Policies */}
          <PoliciesSection
            hotelData={hotelData}
            flightData={flightData}
            groundData={groundData}
          />

          {/* Manage booking (mobile flows in order, desktop moves to column 2) */}
          <ManageBooking
            className={cn("manage-booking")}
            variant={ProductType.PACKAGE}
            itinerary={itinerary}
            isActiveBooking={isActive}
          />
        </div>
      </div>
    </>
  );
};

// ─── Policies ───────────────────────────────────────────────────────────────

function getCarCancellationText(
  cancellationRules: any,
  t: (key: string) => string
): string | null {
  switch (cancellationRules?.RestrictionCancel) {
    case RestrictionCancelEnum.Free:
      return t("core-cars.cancellation.free");
    case RestrictionCancelEnum.Conditional:
    case RestrictionCancelEnum.ConditionalAfterBooking:
      return cancellationRules?.description || null;
    case RestrictionCancelEnum.NonRefundable:
    case RestrictionCancelEnum.Paid:
      return t("core-cars.shopPage.cancellationPolicy.NonRefundable");
    default:
      return null;
  }
}

function getFlightCancellationText(
  flightData: any,
  t: (key: string, params?: Record<string, string | number>) => string,
  formatFiatCurrency: (fiat: { currencyCode: string; value: number }) => string
): string | null {
  const penaltiesInfo =
    flightData?.bookedItinerary?.restrictions?.penaltiesInfo;
  if (penaltiesInfo?.PenaltiesInfo !== "Known") return null;

  const cancelPenalty = penaltiesInfo.cancelPenalty;
  if (!cancelPenalty) return null;

  switch (cancelPenalty.Penalty) {
    case "NoPenalty":
      return t("core-flights.restrictions.penalties.free");
    case "WithFee": {
      const price = formatFiatCurrency({
        currencyCode: cancelPenalty.currency,
        value: cancelPenalty.amount,
      });
      return t("core-flights.restrictions.penalties.withFee", { price });
    }
    case "NotPossible":
      return t("core-flights.restrictions.penalties.nonRefundable");
    default:
      return null;
  }
}

function PoliciesSection({
  hotelData,
  flightData,
  groundData,
}: {
  hotelData: any;
  flightData: any;
  groundData: any;
}) {
  const [, cn] = useModuleBEM(styles, "PackageReservationDetails");
  const { t, formatFiatCurrency } = useI18nContext();

  const items: { value: string; title: string; content: string }[] = [];

  // Stay policy
  const hotelCancellation = hotelData?.reservation?.cancellationPolicy;
  const stayText =
    hotelCancellation?.primaryText ?? hotelCancellation?.secondaryText;
  if (stayText) {
    items.push({
      value: "stay",
      title: t("packageTrips.stayPolicy"),
      content: stayText,
    });
  }

  // Flight policy
  const flightText = getFlightCancellationText(
    flightData,
    t,
    formatFiatCurrency
  );
  if (flightText) {
    items.push({
      value: "flight",
      title: t("packageTrips.flightPolicy"),
      content: flightText,
    });
  }

  // Car policy
  const carText = getCarCancellationText(groundData?.cancellationRules, t);
  if (carText) {
    items.push({
      value: "car",
      title: t("packageTrips.carPolicy"),
      content: carText,
    });
  }

  if (items.length === 0) return null;

  return (
    <div className={cn("policies")}>
      <p className={cn("policies-title")}>{t("packageTrips.policies")}</p>
      <Accordion className={cn("policies-accordion")}>
        {items.map((item) => (
          <AccordionItem key={item.value} value={item.value}>
            <AccordionTrigger className={cn("policies-trigger")}>
              <span className={cn("policies-trigger-title")}>{item.title}</span>
            </AccordionTrigger>
            <AccordionContent>
              <div className={cn("policies-content")}>{item.content}</div>
            </AccordionContent>
          </AccordionItem>
        ))}
      </Accordion>
    </div>
  );
}
